class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.sectionId = this.dataset.sectionId || 'predictive-search';
    this.fallbackSectionId = 'search-suggestions';
    this.usePredictiveApi = window.predictiveSearchApiSupported !== false;

    if (!this.input || !this.predictiveSearchResults) return;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.showSpotlight();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showSpotlight();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (event.isComposing) return;

    if (!this.getQuery().length) {
      this.showSpotlight();
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        event.preventDefault();
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement && (this.statusElement.textContent = '');

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  showSpotlight() {
    if (!this.predictiveSearchResults) return;

    const template = document.getElementById('search-spotlight-template');
    if (!template?.content?.firstElementChild) return;

    this.abortController.abort();
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.predictiveSearchResults.innerHTML = '';
    this.predictiveSearchResults.appendChild(template.content.firstElementChild.cloneNode(true));
    this.setAttribute('results', true);
    this.removeAttribute('loading');
    this.setLiveRegionResults();
    this.open();
  }

  buildPredictiveUrl(searchTerm) {
    const path = this.getRoutePath(routes.predictive_search_url);
    const url = new URL(path, window.location.origin);

    url.searchParams.set('q', searchTerm);
    url.searchParams.set('section_id', this.sectionId);

    return url.toString();
  }

  buildEnglishPredictiveUrl(searchTerm) {
    const path = this.getEnglishPredictivePath();
    const url = new URL(path, window.location.origin);

    url.searchParams.set('q', searchTerm);
    url.searchParams.set('section_id', this.sectionId);

    return url.toString();
  }

  getRoutePath(routeUrl) {
    if (routeUrl.startsWith('http')) {
      return new URL(routeUrl).pathname;
    }

    return routeUrl.startsWith('/') ? routeUrl : `/${routeUrl}`;
  }

  getEnglishPredictivePath() {
    let path = this.getRoutePath(routes.predictive_search_url);

    if (/^\/[a-z]{2}(-[A-Z]{2})?\//.test(path)) {
      path = path.replace(/^\/[a-z]{2}(-[A-Z]{2})?\//, '/en/');
    } else {
      path = `/en${path.startsWith('/') ? path : `/${path}`}`;
    }

    return path;
  }

  buildFallbackUrl(searchTerm) {
    const path = this.getRoutePath(routes.search_url);
    const params = new URLSearchParams();

    params.set('q', searchTerm);
    params.set('options[prefix]', 'last');
    params.set('options[unavailable_products]', 'last');
    params.set('section_id', this.fallbackSectionId);

    return `${window.location.origin}${path}?${params.toString()}`;
  }

  isUsableResultsMarkup(markup) {
    return Boolean(markup?.includes('id="predictive-search-results"'));
  }

  markupHasProducts(markup) {
    return Boolean(markup?.includes('predictive-search-option-product'));
  }

  getProductIndex() {
    if (this.productIndex) return this.productIndex;

    const indexElement = document.getElementById('search-product-index');
    if (!indexElement?.textContent) {
      this.productIndex = [];
      return this.productIndex;
    }

    try {
      this.productIndex = JSON.parse(indexElement.textContent);
    } catch (error) {
      this.productIndex = [];
    }

    return this.productIndex;
  }

  matchesProduct(product, searchTerm) {
    const query = searchTerm.toLowerCase();
    const title = product.title.toLowerCase();
    const normalizedTitle = title.replace(/\s+/g, '');

    if (title.includes(query) || normalizedTitle.includes(query.replace(/\s+/g, ''))) {
      return true;
    }

    return title.split(/[\s\-_/]+/).some((word) => word.startsWith(query));
  }

  searchProductIndex(searchTerm) {
    return this.getProductIndex()
      .filter((product) => this.matchesProduct(product, searchTerm))
      .slice(0, 6);
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;');
  }

  formatSearchFor(searchTerm) {
    const template = window.searchStrings?.searchFor || '「__QUERY__」を検索する';
    return template.replace('__QUERY__', searchTerm);
  }

  buildClientResultsMarkup(searchTerm, products) {
    const productsHeading = window.searchStrings?.productsHeading || '商品';
    const productsMarkup = products
      .map((product, index) => {
        const imageMarkup = product.image
          ? `<img class="predictive-search__image" src="${this.escapeHtml(product.image)}" alt="${this.escapeHtml(product.title)}" width="50" height="50" loading="lazy">`
          : '';

        return `
          <li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
            <a href="${this.escapeHtml(product.url)}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
              ${imageMarkup}
              <div class="predictive-search__item-content predictive-search__item-content--centered">
                <p class="predictive-search__item-heading">${this.escapeHtml(product.title)}</p>
              </div>
            </a>
          </li>
        `;
      })
      .join('');

    const groupsMarkup =
      products.length > 0
        ? `
          <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions">
            <div class="predictive-search__result-group">
              <div>
                <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">${this.escapeHtml(productsHeading)}</h2>
                <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
                  ${productsMarkup}
                </ul>
              </div>
            </div>
          </div>
        `
        : '';

    const liveRegionText =
      products.length > 0 ? `${products.length}件の商品` : `「${searchTerm}」に一致する結果は見つかりませんでした`;

    return `
      <div id="predictive-search-results" role="listbox">
        ${groupsMarkup}
        <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
          <button class="predictive-search__item predictive-search__item--term link link--text" tabindex="-1" role="option" aria-selected="false" type="submit">
            <span data-predictive-search-search-for-text>${this.escapeHtml(this.formatSearchFor(searchTerm))}</span>
          </button>
        </div>
        <span class="hidden" data-predictive-search-live-region-count-value>${this.escapeHtml(liveRegionText)}</span>
      </div>
    `;
  }

  fetchSectionResultsMarkup(searchTerm, url, sectionId) {
    return fetch(url, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }

        return response.text();
      })
      .then((text) => this.extractResultsMarkup(text, sectionId));
  }

  resolveSearchResults(searchTerm, queryKey, initialMarkup) {
    if (this.markupHasProducts(initialMarkup)) {
      this.cacheAndRenderResults(queryKey, initialMarkup);
      return Promise.resolve();
    }

    return this.fetchSectionResultsMarkup(searchTerm, this.buildFallbackUrl(searchTerm), this.fallbackSectionId)
      .then((sectionMarkup) => {
        if (this.markupHasProducts(sectionMarkup)) {
          this.cacheAndRenderResults(queryKey, sectionMarkup);
          return;
        }

        const clientProducts = this.searchProductIndex(searchTerm);
        this.cacheAndRenderResults(queryKey, this.buildClientResultsMarkup(searchTerm, clientProducts));
      })
      .catch(() => {
        const clientProducts = this.searchProductIndex(searchTerm);
        this.cacheAndRenderResults(queryKey, this.buildClientResultsMarkup(searchTerm, clientProducts));
      });
  }

  tryEnglishPredictiveSearch(searchTerm) {
    const url = this.buildEnglishPredictiveUrl(searchTerm);

    return fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) return null;
        return response.text();
      })
      .then((text) => {
        if (!text) return null;
        const markup = this.extractResultsMarkup(text, this.sectionId);
        return this.isUsableResultsMarkup(markup) ? markup : null;
      })
      .catch(() => null);
  }

  fetchPartialSearchResults(searchTerm, queryKey) {
    return this.tryEnglishPredictiveSearch(searchTerm)
      .then((markup) => this.resolveSearchResults(searchTerm, queryKey, markup))
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') return;
        return this.resolveSearchResults(searchTerm, queryKey, null);
      });
  }

  extractResultsMarkup(text, sectionId) {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const section =
      doc.getElementById(`shopify-section-${sectionId}`) ||
      doc.querySelector(`[id^="shopify-section-"][id*="${sectionId}"]`);

    if (!section) return '';

    return section.innerHTML.trim();
  }

  disablePredictiveApi() {
    this.usePredictiveApi = false;
    window.predictiveSearchApiSupported = false;
    this.allPredictiveSearchInstances.forEach((instance) => {
      instance.usePredictiveApi = false;
    });
  }

  cacheAndRenderResults(queryKey, resultsMarkup) {
    if (!resultsMarkup) {
      this.close();
      return;
    }

    this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
      predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
    });
    this.renderSearchResults(resultsMarkup);
  }

  fetchSectionResults(searchTerm, url, sectionId, queryKey) {
    return fetch(url, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = this.extractResultsMarkup(text, sectionId);
        this.cacheAndRenderResults(queryKey, resultsMarkup);
      });
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (!this.usePredictiveApi) {
      this.fetchPartialSearchResults(searchTerm, queryKey).catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') return;
        this.close();
      });
      return;
    }

    fetch(this.buildPredictiveUrl(searchTerm), {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (response.status === 417) {
          this.disablePredictiveApi();
          return this.fetchPartialSearchResults(searchTerm, queryKey);
        }

        if (!response.ok) {
          throw new Error(response.status);
        }

        return response.text().then((text) => {
          let resultsMarkup = this.extractResultsMarkup(text, this.sectionId);

          if (!this.markupHasProducts(resultsMarkup)) {
            const clientProducts = this.searchProductIndex(searchTerm);
            if (clientProducts.length > 0) {
              resultsMarkup = this.buildClientResultsMarkup(searchTerm, clientProducts);
            }
          }

          this.cacheAndRenderResults(queryKey, resultsMarkup);
        });
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') {
          return;
        }
        this.close();
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    if (this.statusElement && this.loadingText) {
      this.setLiveRegionText(this.loadingText);
    }
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!resultsMarkup || !this.predictiveSearchResults) {
      this.close();
      return;
    }

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegion = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion?.textContent) {
      this.setLiveRegionText(liveRegion.textContent);
    }
  }

  getResultsMaxHeight() {
    const anchor =
      this.querySelector('.search-page__box') ||
      this.querySelector('.global-menu__search-box') ||
      document.querySelector('#global-menu .global-menu__search-box') ||
      document.querySelector('.header') ||
      document.querySelector('.section-header');
    this.resultsMaxHeight = window.innerHeight - (anchor?.getBoundingClientRect().bottom ?? 0);
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults?.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
